/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { cloneElement, Component } from 'react';
import { addInputModeListener, addEventListener, containsActiveElement, findFocusable } from '@instructure/ui-dom-utils';
import { logWarn as warn } from '@instructure/console';
import { propTypes, allowedProps } from './props';
import { createChainedFunction } from '@instructure/ui-utils';
/**
---
category: components/utilities
---
**/
class Focusable extends Component {
  constructor(...args) {
    super(...args);
    this._focusListener = null;
    this._blurListener = null;
    this._inputModeListener = null;
    this.state = {
      focused: false,
      focusable: void 0
    };
    this.handleInputModeChange = () => {
      this.forceUpdate();
    };
    this.handleFocus = () => {
      this.removeFocusListener();
      this.setState({
        focused: true
      });
    };
    this.handleBlur = () => {
      this.removeBlurListener();
      this.setState({
        focused: false
      });
    };
    this.ref = null;
    this.attachRef = el => {
      this.ref = el;
    };
  }
  componentDidMount() {
    const focusable = this.focusable,
      focused = this.focused;
    this.addFocusableListeners(focused, focusable);
    this._inputModeListener = addInputModeListener({
      onInputModeChange: this.handleInputModeChange
    });
    this.setState({
      focusable,
      focused
    });
  }
  getSnapshotBeforeUpdate(prevProps) {
    const _this$props = this.props,
      render = _this$props.render,
      children = _this$props.children;
    // prevent blur from firing when focusable element is replaced
    if (prevProps.children !== children || prevProps.render !== render) {
      this.removeFocusableListeners();
    }
    return null;
  }
  componentDidUpdate() {
    const focusable = this.focusable;
    if (!focusable && this.state.focusable) {
      this.removeFocusableListeners();
      this.setState({
        focusable: void 0,
        focused: false
      });
    } else if (focusable !== this.state.focusable) {
      this.removeFocusableListeners();
      if (this.state.focused) {
        ;
        focusable.focus();
      }
      this.addFocusableListeners(this.state.focused, focusable);
      this.setState({
        focusable
      });
    } else {
      this.addFocusableListeners(this.state.focused, focusable);
    }
  }
  componentWillUnmount() {
    if (this._inputModeListener) {
      this._inputModeListener.remove();
      this._inputModeListener = null;
    }
    this.removeFocusableListeners();
  }
  addFocusableListeners(focused, focusable) {
    if (!focusable) return;
    if (focused && !this._blurListener) {
      this._blurListener = addEventListener(focusable, 'blur', this.handleBlur, true);
    } else if (!this._focusListener) {
      this._focusListener = addEventListener(focusable, 'focus', this.handleFocus, true);
    }
  }
  removeFocusableListeners() {
    this.removeFocusListener();
    this.removeBlurListener();
  }
  removeFocusListener() {
    if (this._focusListener) {
      this._focusListener.remove();
      this._focusListener = null;
    }
  }
  removeBlurListener() {
    if (this._blurListener) {
      this._blurListener.remove();
      this._blurListener = null;
    }
  }
  get focused() {
    return containsActiveElement(this);
  }
  get focusable() {
    const focusableArr = findFocusable(this, () => true, true) || [];
    const focusableCount = focusableArr && focusableArr.length || 0;
    warn(focusableCount === 1, `[Focusable] Exactly one focusable child is required (${focusableCount} found).`);
    const focusable = focusableArr ? focusableArr[0] : false;
    if (focusable && typeof focusable.focus === 'function') {
      return focusable;
    } else {
      return void 0;
    }
  }
  get focusVisible() {
    const _this$state = this.state,
      focusable = _this$state.focusable,
      focused = _this$state.focused;
    return this.isFocusVisible(focused, focusable);
  }
  focus() {
    const focusable = this.focusable;
    if (focusable) {
      focusable.focus();
    }
  }
  isFocusVisible(focused, focusable) {
    if (!focusable || !focused) return false;

    // always show focus for keyboard input mode
    if (this._inputModeListener && this._inputModeListener.isKeyboardMode()) return true;
    // note: the type property exist on input fields like HtmlInputElement
    const tagName = focusable.tagName,
      type = focusable.type,
      isContentEditable = focusable.isContentEditable;
    if (tagName == 'INPUT' && Focusable.inputTypes[type]) {
      return true;
    }
    if (tagName == 'TEXTAREA') {
      return true;
    }
    return isContentEditable;
  }
  render() {
    const _this$props2 = this.props,
      children = _this$props2.children,
      _this$props2$render = _this$props2.render,
      render = _this$props2$render === void 0 ? children : _this$props2$render;
    const _this$state2 = this.state,
      focusable = _this$state2.focusable,
      focused = _this$state2.focused;
    if (typeof render === 'function') {
      const rendered = render({
        focused,
        focusable,
        focusVisible: this.isFocusVisible(focused, focusable),
        attachRef: this.attachRef
      });
      return /*#__PURE__*/cloneElement(rendered, {
        ref: rendered.ref ? createChainedFunction(rendered.ref, this.attachRef) : this.attachRef
      });
    } else {
      return null;
    }
  }
}
Focusable.displayName = "Focusable";
Focusable.propTypes = propTypes;
Focusable.allowedProps = allowedProps;
Focusable.defaultProps = {
  children: null
};
Focusable.inputTypes = {
  text: true,
  search: true,
  url: true,
  tel: true,
  email: true,
  password: true,
  number: true,
  date: true,
  month: true,
  week: true,
  time: true,
  datetime: true,
  'datetime-local': true
};
export default Focusable;
export { Focusable };